import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { setTariffs } from '@helper';
import {
  Ders,
  EssParameters,
  ForecastLoadProfile,
  GridParameters,
  LoadProfile,
  LoadProfileJson,
  MaxSiteParameters,
  MinSiteParameters,
  Result,
  Scenario,
  ScenarioPrograms,
  SolarLoadProfile,
  Tariff,
  TariffParameters,
  CostTimeseries,
  TariffCharts,
  Project,
} from '@model';
import {
  ForecastService,
  GlobalAlertService,
  LoadProfileService,
  ScenarioService,
  SolarPvService,
} from '@service';

/**
 * First batch result type from loading existing scenario core data
 */
export type FirstBatchResult = [
  Scenario,
  Result[],
  Tariff[],
  Ders[],
  EssParameters,
  TariffParameters,
  MinSiteParameters,
  MaxSiteParameters,
  GridParameters,
  ScenarioPrograms,
];

/**
 * Second batch result type from loading profiles and related data
 */
export type SecondBatchResult = [
  Scenario,
  Project,
  LoadProfile | null,
  LoadProfileJson[] | null,
  SolarLoadProfile | null,
  LoadProfile | null,
  LoadProfileJson[] | null,
  ForecastLoadProfile | null,
  ForecastLoadProfile | null,
];

/**
 * Enriches scenario with first batch results (core data)
 * This is a pure function that returns a new scenario reference with enriched data
 * @param scenario - The scenario to enrich
 * @param firstBatch - The first batch results
 * @returns The same scenario reference with mutated properties
 */
export function enrichScenarioWithFirstBatch(
  scenario: Scenario,
  firstBatch: FirstBatchResult,
): Scenario {
  const [
    ,
    results,
    tariffs,
    ders,
    essParameters,
    tariffParameters,
    minSiteParameters,
    maxSiteParameters,
    gridParameters,
    programs,
  ] = firstBatch;

  scenario.results = results;
  scenario.ders = ders;
  scenario.essParameters = essParameters;
  scenario.tariffParameters = tariffParameters;
  scenario.minLoadSiteParameters = minSiteParameters;
  scenario.maxLoadSiteParameters = maxSiteParameters;
  scenario.gridParameters = gridParameters;
  scenario.programs = programs;
  setTariffs(scenario, ...tariffs);

  return scenario;
}

/**
 * Enriches scenario with second batch results (profiles and related data)
 * @param scenario - The scenario to enrich
 * @param secondBatch - The second batch results
 * @returns The same scenario reference with mutated properties
 */
export function enrichScenarioWithSecondBatch(
  scenario: Scenario,
  secondBatch: SecondBatchResult,
): Scenario {
  const [
    ,
    ,
    loadProfile,
    loadProfileJson,
    pvloadProfile,
    adjustedPvLoadProfile,
    adjustedPvLoadProfileJson,
    forecastLoadProfile,
    forecastPvLoadProfile,
  ] = secondBatch;

  scenario.loadProfile = loadProfile;
  if (scenario.loadProfile && loadProfileJson) {
    scenario.loadProfile.json = loadProfileJson;
  }
  scenario.pvloadProfile = pvloadProfile;
  scenario.adjustedPvLoadProfile = adjustedPvLoadProfile;
  if (scenario.adjustedPvLoadProfile && adjustedPvLoadProfileJson) {
    scenario.adjustedPvLoadProfile.json = adjustedPvLoadProfileJson;
  }
  scenario.forecastLoadProfile = forecastLoadProfile;
  scenario.forecastPvLoadProfile = forecastPvLoadProfile;

  return scenario;
}

/**
 * Creates conditional load profile observables based on scenario properties
 */
export function createConditionalLoadProfileObservables(
  scenario: Scenario,
  loadProfileService: LoadProfileService,
  solarPvService: SolarPvService,
  forecastService: ForecastService,
): {
  adjustedLoadProfile$: Observable<LoadProfile | null>;
  adjustedJson$: Observable<LoadProfileJson[] | null>;
  loadProfile$: Observable<LoadProfile | null>;
  loadProfileJson$: Observable<LoadProfileJson[] | null>;
  pvLoadProfile$: Observable<SolarLoadProfile | null>;
  forecastedLoadProfile$: Observable<ForecastLoadProfile | null>;
  forecastedPvLoadProfile$: Observable<ForecastLoadProfile | null>;
} {
  const adjustedLoadProfile$ = scenario.adjustedPvLoadId
    ? loadProfileService.loadProfile$(scenario.adjustedPvLoadId)
    : of(null);

  const adjustedJson$ = scenario.adjustedPvLoadId
    ? loadProfileService.getLoadProfileJson(scenario.adjustedPvLoadId)
    : of(null);

  const loadProfile$ = scenario.loadprofileId
    ? loadProfileService.loadProfile$(scenario.loadprofileId)
    : of(null);

  const loadProfileJson$ = scenario.loadprofileId
    ? loadProfileService.getLoadProfileJson(scenario.loadprofileId)
    : of(null);

  const pvLoadProfile$ = scenario.pvloadId
    ? solarPvService.pvLoadProfile$(scenario.pvloadId)
    : of(null);

  const forecastedLoadProfile$ = scenario.forecastLoadId
    ? forecastService.forecastDispatch$(scenario.forecastLoadId)
    : of(null);

  const forecastedPvLoadProfile$ = scenario.forecastPvId
    ? forecastService.forecastDispatch$(scenario.forecastPvId)
    : of(null);

  return {
    adjustedLoadProfile$,
    adjustedJson$,
    loadProfile$,
    loadProfileJson$,
    pvLoadProfile$,
    forecastedLoadProfile$,
    forecastedPvLoadProfile$,
  };
}

/**
 * Creates the cost timeseries observable with proper error handling
 */
export function createCostTimeseriesObservable(
  scenarioService: ScenarioService,
  scenarioId: string,
  scenario: Scenario,
): Observable<CostTimeseries[]> {
  return scenarioService.getCostTimeseries$(scenarioId).pipe(
    map(response => {
      if (!response || !Array.isArray(response)) {
        return [];
      }
      return response;
    }),
    tap((costTimeseries: CostTimeseries[]) => {
      scenario.costTimeseries = costTimeseries;
    }),
    catchError(error => {
      console.error('Error fetching cost timeseries:', error);
      return of([]);
    }),
  );
}

/**
 * Creates the cost tariff charts observable with proper error handling
 */
export function createCostTariffChartsObservable(
  scenarioService: ScenarioService,
  scenarioId: string,
  scenario: Scenario,
): Observable<TariffCharts[]> {
  return scenarioService.getCostTariffCharts$(scenarioId).pipe(
    map(response => {
      if (!response || !Array.isArray(response)) {
        return [];
      }
      return response;
    }),
    tap((costTariffCharts: TariffCharts[]) => {
      scenario.costTariffCharts = costTariffCharts;
    }),
    catchError(error => {
      console.error('Error fetching cost tariff charts:', error);
      return of([]);
    }),
  );
}

/**
 * Common error handler for resolver operations
 * @param err - The error that occurred
 * @param globalAlertService - Service to display errors to user
 * @returns Observable that throws the formatted error
 */
export function handleResolverError(
  err: unknown,
  globalAlertService: GlobalAlertService,
): Observable<never> {
  const errorMessage =
    err instanceof Error ? err.message : JSON.stringify(err);
  globalAlertService.setError(errorMessage);
  return throwError(() => new Error(errorMessage));
}
